import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';

import { Box } from 'components/ui';
import CartLayout from 'components/RwdBasket/Carts/CartLayout/CartLayout';
import SDDDeliveryInfo from 'components/RwdBasket/DeliveryInfo/SDD/SDDDeliveryInfo';
import TextZone from 'components/RwdBasket/Carts/SDDCart/TextZone/TextZone';
import SDUSkuItem from 'components/RwdBasket/Carts/SDDCart/SkuItem/SDUSkuItem';
import SDDSkuItem from 'components/RwdBasket/Carts/SDDCart/SkuItem/SDDSkuItem';
import ConfirmationBox from 'components/RwdBasket/Carts/CartLayout/ConfirmationBox/ConfirmationBox';
import SaveForLaterConfirmationBox from 'components/RwdBasket/Carts/CartLayout/SaveForLaterConfirmationBox';
import RewardSkuItem from 'components/RwdBasket/Carts/CartLayout/SkuItem/RewardSkuItem';

import localeUtils from 'utils/LanguageLocale';
import addressUtils from 'utils/Address';
import skuUtils from 'utils/Sku';
import deliveryOptionsUtils from 'utils/DeliveryOptions';

import getCallbacks from 'components/RwdBasket/Carts/CartLayout/SkuItem/Buttons/getCallbacks';
import compConstants from 'components/constants';
import SDDCartEN from 'components/RwdBasket/Carts/SDDCart/locales/SDDCart_en_US';
import SDDCartFR from 'components/RwdBasket/Carts/SDDCart/locales/SDDCart_fr_CA';
import basketConstants from 'constants/Basket';
import BasketBindings from 'analytics/bindingMethods/pages/basket/BasketBindings';

const { formatZipCode } = addressUtils;
const { getLocalisedDataFromResource } = localeUtils;
const { BasketType } = basketConstants;
const { isNDDNewDeliveryName } = deliveryOptionsUtils;
const allCallbacks = getCallbacks({ isBopis: false, basketType: BasketType.SameDay });

class SDDCart extends BaseClass {
    constructor(props) {
        super(props);

        this.state = {
            outOfStockMap: {}
        };
    }

    componentDidMount() {
        this.props.errors.sameDayRewardErrors.forEach(error => {
            BasketBindings.triggerRewardErrorAnalytics(this.props.items, error);
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.items === this.props.items) {
            return;
        }

        const { outOfStockMap } = this.state;
        const currentKeys = Object.keys(outOfStockMap);

        if (currentKeys.length === 0) {
            return;
        }

        const currentSkuIds = new Set(this.props.items.map(item => item.sku.skuId));
        const prunedMap = currentKeys.reduce((acc, skuId) => {
            if (currentSkuIds.has(skuId)) {
                acc[skuId] = outOfStockMap[skuId];
            }

            return acc;
        }, {});

        if (Object.keys(prunedMap).length !== currentKeys.length) {
            this.setState({ outOfStockMap: prunedMap });
        }
    }

    handleSddSkuIsOOSChange = (skuId, isOOS) => {
        this.setState({
            outOfStockMap: {
                ...this.state.outOfStockMap,
                [skuId]: isOOS
            }
        });
    };

    skuComponentMapper = (getText, itemErrorMap) => {
        const {
            items,
            isUserSDUTrialEligible,
            itemDeliveryMethod,
            preferredStoreInfo,
            preferredZipCode,
            userId,
            hasMetFreeShippingThreshhold,
            isSignedIn,
            isSignedInBIUser,
            sameDayIsAvailable,
            isOmniRewardEnabled,
            addItemSavingsBasketExperience
        } = this.props;

        return items.map((item, index) => {
            item.error = itemErrorMap.get(item.commerceId);
            item.enablePageRenderTracking = index < compConstants.PAGE_RENDER_TRACKING_LIMIT;

            const props = {
                key: item.sku.skuId,
                item: item,
                changeMethodLabel: getText('changeMethod'),
                callbacks: {
                    ...allCallbacks,
                    handleSddSkuIsOOSChange: this.handleSddSkuIsOOSChange
                },
                itemDeliveryMethod,
                preferredStoreInfo,
                preferredZipCode,
                userId,
                hasMetFreeShippingThreshhold,
                isSignedIn,
                isSignedInBIUser,
                sameDayIsAvailable,
                isOutOfStock: this.state.outOfStockMap[item.sku.skuId],
                isOmniRewardEnabled,
                addItemSavingsBasketExperience
            };

            if (skuUtils.isSDU(item.sku)) {
                return (
                    <SDUSkuItem
                        isUserSDUTrialEligible={isUserSDUTrialEligible}
                        getText={getText}
                        {...props}
                    />
                );
            }

            if (skuUtils.isBiReward(item.sku)) {
                return <RewardSkuItem {...props} />;
            }

            if (skuUtils.isStandardProduct(item.sku)) {
                return <SDDSkuItem {...props} />;
            }

            throw new Error(`SDDCart sku mapper received sku of type: ${item.sku.type}`);
        });
    };

    showHeadlineError = () => {
        const { sameDayIsAvailable } = this.props;
        const oosItemExists = Object.values(this.state.outOfStockMap).some(bool => bool);
        // Check if reducer default null has changed to a boolean from user or zip call
        const zipCodeEligibilityDetermined = typeof sameDayIsAvailable === 'boolean';

        if (zipCodeEligibilityDetermined) {
            return oosItemExists || !sameDayIsAvailable;
        }

        return oosItemExists;
    };

    handleDismissBasketConfirmation = () => {
        this.props.clearBasketConfirmation();
    };

    handleUndoSaveForLater = () => {
        const { basketConfirmation, userId, listId, undoAddToSaveForLater } = this.props;

        if (basketConfirmation) {
            undoAddToSaveForLater({
                ...basketConfirmation,
                userId,
                listId
            });
        }
    };

    handleOpenModal = () => {
        this.props.showSavedForLaterModal({ isOpen: true });
    };

    render() {
        const {
            items,
            totalQuantity,
            sameDayTitle,
            sameDayDeliveryMessage,
            isUserSDUTrialEligible,
            preferredZipCode,
            itemDeliveryMethod,
            errors,
            itemErrorMap,
            confirmationBoxInfo,
            infoModalCallbacks,
            sameDayIsAvailable,
            showBasketGreyBackground,
            backgroundColor,
            basketConfirmation,
            ...props
        } = this.props;

        const getText = getLocalisedDataFromResource(SDDCartEN, SDDCartFR);
        const sddTitle = isNDDNewDeliveryName() ? getText('fastDelivery') : sameDayTitle || getText('sameDayDelivery');
        const isServiceUnavailable = itemErrorMap.has('serviceUnavailable');

        const subHeader = !isServiceUnavailable && (
            <Box
                paddingLeft={'36px'}
                marginTop={['2px', 1]}
            >
                <SDDDeliveryInfo
                    zipCode={formatZipCode(preferredZipCode)}
                    message={sameDayDeliveryMessage}
                    isSduAvailable={errors?.isAvailable}
                    sameDayIsAvailable={sameDayIsAvailable}
                    entry='basket page'
                />
            </Box>
        );

        return (
            <CartLayout
                {...(showBasketGreyBackground && { backgroundColor: backgroundColor })}
                cartType='sameday'
                title={`${sddTitle} (${totalQuantity})`}
                infoModalCallbacks={infoModalCallbacks}
                iconName={this.showHeadlineError() ? 'alert' : 'bag'}
                iconColor={this.showHeadlineError() ? 'red' : null}
                confirmationBox={
                    <>
                        <ConfirmationBox
                            {...confirmationBoxInfo}
                            onUndoChangeMethod={allCallbacks.onUndoChangeMethod}
                            itemDeliveryMethod={itemDeliveryMethod}
                        />
                        <SaveForLaterConfirmationBox
                            basketConfirmation={basketConfirmation}
                            basketType={BasketType.SameDay}
                            onDismiss={this.handleDismissBasketConfirmation}
                            onUndo={this.handleUndoSaveForLater}
                            onOpenModal={this.handleOpenModal}
                        />
                    </>
                }
                skuItemComponents={this.skuComponentMapper(getText, itemErrorMap)}
                subHeader={subHeader}
                errors={errors}
                itemErrorMap={itemErrorMap}
                textZone={
                    <TextZone
                        isUserSDUTrialEligible={isUserSDUTrialEligible}
                        {...props}
                    />
                }
            />
        );
    }
}

export default wrapComponent(SDDCart, 'SDDCart', true);
